import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import FPS_Details from "./FPS_Details";
import progressGif from "../../images/progress.gif";

const AFSO_FPS = ({ distCode, distName }) => {
  const [afsoData, setAfsoData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedAfso, setSelectedAfso] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("T");
  const [fpsLoading, setFpsLoading] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const fpsDetailsRef = useRef(null);


  useEffect(() => {
    if (!distCode) return;
    setError(null);

    axios
      .get(`${process.env.REACT_APP_URL_FPS}/api/fps/afsoSummary`, {
        params: { distCode },
      })
      .then((res) => {
        setAfsoData(res.data);
      })
      .catch((err) => {
        console.error("Error fetching AFSO summary:", err);
        setError("Failed to load AFSO summary");
      });
  }, [distCode]);


  useEffect(() => {
    if (selectedAfso && !fpsLoading && fpsDetailsRef.current) {
      fpsDetailsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedAfso, fpsLoading]);


  const handleCellClick = (afsoName, officeCode, status) => {
    setFpsLoading(true);
    setSelectedAfso(null);

    setTimeout(() => {
      setSelectedAfso({ afsoName, officeCode });
      setSelectedStatus(status);
      setFpsLoading(false);
    }, 100);
  };

  const baseLinkStyle = {
    textDecoration: "underline",
    cursor: "pointer",
  };

  if (error) return <div style={{ color: "red" }}>{error}</div>;

  if (afsoData.length === 0) {
    return <p style={{ textAlign: "center", marginTop: "50px" }}>No records found</p>;
  }

  const totals = afsoData.reduce(
    (acc, row) => {
      const totalShops = row.totalShops || 0;
      const deviceMapped = row.mappedShops || 0;
      const deviceNotMapped = totalShops - deviceMapped;
      const dealerMapped = row.dealerMappedShops || 0;
      const dealerNotMapped = totalShops - dealerMapped;
      const nom1Mapped = row.nom1MappedShops || 0;
      const nom1NotMapped = totalShops - nom1Mapped;
      const nom2Mapped = row.nom2MappedShops || 0;
      const nom2NotMapped = totalShops - nom2Mapped;

      return {
        total: acc.total + totalShops,
        deviceMapped: acc.deviceMapped + deviceMapped,
        deviceNotMapped: acc.deviceNotMapped + deviceNotMapped,
        dealerMapped: acc.dealerMapped + dealerMapped,
        dealerNotMapped: acc.dealerNotMapped + dealerNotMapped,
        nom1Mapped: acc.nom1Mapped + nom1Mapped,
        nom1NotMapped: acc.nom1NotMapped + nom1NotMapped,
        nom2Mapped: acc.nom2Mapped + nom2Mapped,
        nom2NotMapped: acc.nom2NotMapped + nom2NotMapped,
        weightingEnabled: acc.weightingEnabled + (row.weightingEnabled || 0),
        weightingNotEnabled: acc.weightingNotEnabled + (row.weightingNotEnabled || 0),
        grainStEnabled: acc.grainStEnabled + (row.grainStEnabled || 0),
        grainStNotEnabled: acc.grainStNotEnabled + (row.grainStNotEnabled || 0),
      };
    },
    {
      total: 0,
      deviceMapped: 0,
      deviceNotMapped: 0,
      dealerMapped: 0,
      dealerNotMapped: 0,
      nom1Mapped: 0,
      nom1NotMapped: 0,
      nom2Mapped: 0,
      nom2NotMapped: 0,
      weightingEnabled: 0,
      weightingNotEnabled: 0,
      grainStEnabled: 0,
      grainStNotEnabled: 0,
    }
  );

  return (
    <div style={{ paddingTop: "30px", paddingBottom: "300px" }}>
      <table
        align="center"
        width="70%"
        border="1"
        cellPadding="5"
        style={{ borderCollapse: "collapse" }}
      >
        <thead>
          {distName && (
            <tr
              className="tableheader">
              <th colSpan="15"

              >
                <span style={{ color: "orange" }}>{distName}</span> FPS Details
              </th>
            </tr>
          )}

          <tr className="tableheader">
            <th rowSpan="2">Sl.No</th>
            <th rowSpan="2">AFSO</th>
            <th rowSpan="2">Total</th>
            <th colSpan="2">Device</th>
            <th colSpan="2">Dealer UID</th>
            <th colSpan="2">Nominee 1 UID</th>
            <th colSpan="2">Nominee 2 UID</th>
            <th colSpan="2">Weighing</th>
            <th colSpan="2">Grain ATM</th>
          </tr>
          <tr className="tableheader">
            <th>Mapped</th>
            <th>Not<br />Mapped</th>
            <th>Mapped</th>
            <th>Not<br />Mapped</th>
            <th>Mapped</th>
            <th>Not<br />Mapped</th>
            <th>Mapped</th>
            <th>Not<br />Mapped</th>
            <th>Enabled</th>
            <th>Not<br />Enabled</th>
            <th>Enabled</th>
            <th>Not<br />Enabled</th>
          </tr>
        </thead>

        <tbody>
          {afsoData.map((row, index) => {
            const total = row.totalShops || 0;
            const deviceMapped = row.mappedShops || 0;
            const deviceNotMapped = total - deviceMapped;
            const dealerMapped = row.dealerMappedShops || 0;
            const dealerNotMapped = total - dealerMapped;
            const nom1Mapped = row.nom1MappedShops || 0;
            const nom1NotMapped = total - nom1Mapped;
            const nom2Mapped = row.nom2MappedShops || 0;
            const nom2NotMapped = total - nom2Mapped;

            return (
              <tr key={index}>
                <td align="center">{index + 1}</td>
                <td
                  style={{ ...baseLinkStyle, color: hoveredCell === `afso-${index}` ? 'red' : 'inherit' }}
                  onMouseEnter={() => setHoveredCell(`afso-${index}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={() =>
                    handleCellClick(row.afsoName, row.officeCode, "T")
                  }
                >
                  {row.afsoName}
                </td>
                <td align="right">{total}</td>
                <td align="right">{deviceMapped}</td>
                <td
                  align="right"
                  style={{ ...baseLinkStyle, color: hoveredCell === `tn-${index}` ? 'red' : 'inherit' }}
                  onMouseEnter={() => setHoveredCell(`tn-${index}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={() =>
                    handleCellClick(row.afsoName, row.officeCode, "TN")
                  }
                >
                  {deviceNotMapped}
                </td>
                <td align="right">{dealerMapped}</td>
                <td
                  align="right"
                  style={{ ...baseLinkStyle, color: hoveredCell === `dn-${index}` ? 'red' : 'inherit' }}
                  onMouseEnter={() => setHoveredCell(`dn-${index}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={() =>
                    handleCellClick(row.afsoName, row.officeCode, "DN")
                  }
                >
                  {dealerNotMapped}
                </td>
                <td align="right">{nom1Mapped}</td>
                <td
                  align="right"
                  style={{ ...baseLinkStyle, color: hoveredCell === `n1-${index}` ? 'red' : 'inherit' }}
                  onMouseEnter={() => setHoveredCell(`n1-${index}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={() =>
                    handleCellClick(row.afsoName, row.officeCode, "N1")
                  }
                >
                  {nom1NotMapped}
                </td>
                <td align="right">{nom2Mapped}</td>
                <td
                  align="right"
                  style={{ ...baseLinkStyle, color: hoveredCell === `n2-${index}` ? 'red' : 'inherit' }}
                  onMouseEnter={() => setHoveredCell(`n2-${index}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={() =>
                    handleCellClick(row.afsoName, row.officeCode, "N2")
                  }
                >
                  {nom2NotMapped}
                </td>
                <td
                  align="right"
                  style={{ ...baseLinkStyle, color: hoveredCell === `we-${index}` ? 'red' : 'inherit' }}
                  onMouseEnter={() => setHoveredCell(`we-${index}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={() =>
                    handleCellClick(row.afsoName, row.officeCode, "WE")
                  }
                >
                  {row.weightingEnabled}
                </td>
                <td
                  align="right"
                  style={{ ...baseLinkStyle, color: hoveredCell === `wne-${index}` ? 'red' : 'inherit' }}
                  onMouseEnter={() => setHoveredCell(`wne-${index}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={() =>
                    handleCellClick(row.afsoName, row.officeCode, "WNE")
                  }
                >
                  {row.weightingNotEnabled}
                </td>
                <td
                  align="right"
                  style={{ ...baseLinkStyle, color: hoveredCell === `atme-${index}` ? 'red' : 'inherit' }}
                  onMouseEnter={() => setHoveredCell(`atme-${index}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={() =>
                    handleCellClick(row.afsoName, row.officeCode, "ATME")
                  }
                >
                  {row.grainStEnabled}
                </td>
                <td
                  align="right"
                  style={{ ...baseLinkStyle, color: hoveredCell === `atmne-${index}` ? 'red' : 'inherit' }}
                  onMouseEnter={() => setHoveredCell(`atmne-${index}`)}
                  onMouseLeave={() => setHoveredCell(null)}
                  onClick={() =>
                    handleCellClick(row.afsoName, row.officeCode, "ATMNE")
                  }
                >
                  {row.grainStNotEnabled}
                </td>
              </tr>
            );
          })}
          <tr
            className="tableheader"
          >
            <td colSpan="2" align="center">
              Total
            </td>
            <td align="right">{totals.total}</td>
            <td align="right">{totals.deviceMapped}</td>
            <td align="right">{totals.deviceNotMapped}</td>
            <td align="right">{totals.dealerMapped}</td>
            <td align="right">{totals.dealerNotMapped}</td>
            <td align="right">{totals.nom1Mapped}</td>
            <td align="right">{totals.nom1NotMapped}</td>
            <td align="right">{totals.nom2Mapped}</td>
            <td align="right">{totals.nom2NotMapped}</td>
            <td align="right">{totals.weightingEnabled}</td>
            <td align="right">{totals.weightingNotEnabled}</td>
            <td align="right">{totals.grainStEnabled}</td>
            <td align="right">{totals.grainStNotEnabled}</td>
          </tr>
        </tbody>
      </table>

      {fpsLoading && (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <img src={progressGif} alt="Loading..." width="250" height="15" />
        </div>
      )}
      {selectedAfso && !fpsLoading && (
        <div ref={fpsDetailsRef} style={{ marginTop: "50px" }}>
          <FPS_Details
            officeCode={selectedAfso.officeCode}
            status={selectedStatus}
            distName={distName}
            afsoName={selectedAfso.afsoName}
          />
        </div>
      )}

    </div>
  );
};

export default AFSO_FPS;